import {CSSProperties} from "react";
import {apiBindings} from "../../utils/constants";
import {
    AVATAR_ELEMENT_TYPES, AvatarCatalogue, AvatarElement, AvatarElementType, AvatarPicks, layerBox, resolveElement,
} from "../../models/avatars.model";

// A fit being dragged or slid, previewed before it is committed to the draft.
export interface TransientFit {
    type: AvatarElementType;
    id: number;
    scale: number;
    offsetX: number;
    offsetY: number;
}

export function elementImageUrl(element: AvatarElement): string {
    return `${apiBindings.images}${element.fileName}`;
}

// The element a pick draws, with any in-flight fit applied.
export function drawnElement(catalogue: AvatarCatalogue, type: AvatarElementType, picks: AvatarPicks,
                             transient?: TransientFit): AvatarElement | undefined {
    const id = picks[type];
    if (id === undefined) return undefined;
    const element = resolveElement(catalogue.elements[type], id);
    if (!element || !transient || transient.type !== type || transient.id !== element.id) return element;
    return {...element, scale: transient.scale, offsetX: transient.offsetX, offsetY: transient.offsetY};
}

// One avatar, drawn the way the client's AvatarView draws it: each picked layer back
// to front, placed by its fit (layerBox), on a square `size` wide. A pick naming a
// deleted element draws the type's first element, as in game. Clipped to the square
// unless `overflow` is set (the stage shows what spills out, dimmed).
function AvatarLayers(props: {
    catalogue: AvatarCatalogue;
    picks: AvatarPicks;
    size: number;
    transient?: TransientFit;
    overflow?: boolean;
    style?: CSSProperties;
}) {
    return (
        <div style={{
            position: 'relative', width: props.size, height: props.size, flexShrink: 0,
            overflow: props.overflow ? 'visible' : 'hidden',
            ...props.style,
        }}>
            {AVATAR_ELEMENT_TYPES.map(type => {
                const element = drawnElement(props.catalogue, type, props.picks, props.transient);
                if (!element) return null;
                const box = layerBox(element, props.size);
                return (
                    <img key={type} src={elementImageUrl(element)} alt={''} draggable={false}
                         style={{position: 'absolute', ...box, objectFit: 'contain', pointerEvents: 'none'}}/>
                );
            })}
        </div>
    );
}

export default AvatarLayers;
